import { useTracking } from '@packages/tracking';
import type { EECCheckoutEvent, GtmProduct, ProductCustomDimensions } from '@packages/tracking';
import type { FC } from 'react';
import { useEffect } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { BasketTrackingFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketTrackingFragmentFragmentDoc';

/* GraphQL */ `
  fragment BasketTrackingFragment on Basket {
    lineItems {
      quantity
      availability {
        message
      }
      price {
        value
      }
      singlePrice {
        amount
      }
      product {
        variation
        orderNumber
        imageUrl
        name
        sku
      }
      extra {
        brand
        breadcrumbs
        category
        description
        discountedTotalPrice
        hasOldPrice
        mastersku
        mkz
        promotion
        size
        sourceShopPath
        variation
      }
    }
  }
`;

type TrackingProps = {
  eecType: 'EECCheckout' | 'EECPurchase';
  step: number;
  data?: FragmentType<typeof BasketTrackingFragmentFragmentDoc>;
};

export const Tracking: FC<TrackingProps> = ({ eecType, step, data }) => {
  const dispatchEvent = useTracking();
  const fragmentData = getFragmentData(BasketTrackingFragmentFragmentDoc, data);
  const lineItems = fragmentData?.lineItems || [];

  useEffect(() => {
    const products: Array<GtmProduct & Partial<ProductCustomDimensions>> = lineItems.map(
      (lineItem) => ({
        dimension12: lineItem.price?.value === 0 ? 'ja' : 'nein',
        dimension15: lineItem.extra?.size || undefined,
        dimension16: lineItem.product.variation || undefined,
        dimension18: lineItem.extra?.hasOldPrice || undefined,
        dimension19: lineItem.extra?.promotion || undefined,
        dimension20: lineItem.availability.message || undefined,
        dimension22: lineItem.extra?.discountedTotalPrice || undefined,
        dimension24: lineItem.product.sku,
        brand: lineItem.extra?.brand || undefined,
        category: (lineItem.extra?.breadcrumbs || []).join('/'),
        id: lineItem.product.orderNumber,
        imageUrl: lineItem.product.imageUrl,
        masterSku: lineItem.extra?.mastersku || undefined,
        mkz: lineItem.extra?.mkz,
        name: lineItem.product.name,
        price: (lineItem.singlePrice?.amount || 0) / 100,
        priceAfterDiscount: lineItem.extra?.discountedTotalPrice || undefined,
        quantity: lineItem.quantity,
        sku: lineItem.product.sku,
        sourceShopPath: lineItem.extra?.breadcrumbs || undefined,
        sourceShopId: lineItem.extra?.sourceShopPath || undefined,
        variant: lineItem.product.variation || undefined,
      }),
    );

    if (eecType === 'EECPurchase') {
      // TODO relevant for order confirmation
    } else {
      dispatchEvent<EECCheckoutEvent>({
        event: 'EECcheckout',
        payment: {
          art: '', // payment?.paymentType,
          valuta: false, // payment?.delayedPayment || false,
        },
        ecommerce: {
          checkout: {
            actionField: {
              step,
              action: 'checkout',
              option: '', // delivery || payment?.paymentType,
            },
            products,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
